import React, { useState, useEffect, useCallback } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Table, 
  Button, 
  Modal, 
  Form, 
  Alert, 
  Spinner,
  Badge,
  Pagination,
  InputGroup,
  FormControl
} from 'react-bootstrap';
import { FiEdit2, FiTrash2, FiUserCheck, FiUserX, FiPlus } from 'react-icons/fi';
import { productsAPI } from '../services/api';
import { Product, ProductsResponse } from '../types';
import { useAuth } from '../contexts/AuthContext';

const Products: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    productName: '',
    description: '',
    waterTankerEnabled: false,
    tankerEntries: [] as Array<{ rfid: string; tankerDetails: string }>,
    // product-level formula removed
  });

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      const response: ProductsResponse = await productsAPI.getAll(
        currentPage, 
        10, 
        searchTerm
      );
      setProducts(response.products);
      setTotalPages(response.pagination.total);
    } catch (error) {
      setError('Failed to fetch products');
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const resetForm = () => {
    setFormData({
      productName: '',
      description: '',
      waterTankerEnabled: false,
      tankerEntries: []
      
    });
    setEditingProduct(null);
  };

  const handleShowModal = async (product?: Product) => {
    if (product) {
      try {
        const fresh = await productsAPI.getById(product.id);
        setEditingProduct(fresh as Product);
        setFormData({
          productName: (fresh as any).productName,
          description: (fresh as any).description || '',
          waterTankerEnabled: !!(fresh as any).waterTankerEnabled,
          tankerEntries: Array.isArray((fresh as any).tankerEntries) ? (fresh as any).tankerEntries.map((p: any) => ({ rfid: p.rfid || '', tankerDetails: p.tankerDetails || '' })) : []
        });
      } catch (err) {
        // fallback to provided product if fetch fails
        setEditingProduct(product);
        setFormData({
          productName: product.productName,
          description: product.description || '',
          waterTankerEnabled: !!product.waterTankerEnabled,
          tankerEntries: Array.isArray(product.tankerEntries) ? product.tankerEntries.map((p) => ({ rfid: p.rfid || '', tankerDetails: p.tankerDetails || '' })) : []
        });
      }
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
    setError('');
    setSuccess('');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  const addTankerEntry = () => {
    setFormData(prev => ({
      ...prev,
      tankerEntries: [...prev.tankerEntries, { rfid: '', tankerDetails: '' }]
    }));
  };

  const removeTankerEntry = (idx: number) => {
    setFormData(prev => ({
      ...prev,
      tankerEntries: prev.tankerEntries.filter((_, i) => i !== idx)
    }));
  };

  const handleTankerEntryChange = (idx: number, field: 'rfid' | 'tankerDetails', value: string) => {
    setFormData(prev => ({
      ...prev,
      tankerEntries: prev.tankerEntries.map((entry, i) => i === idx ? { ...entry, [field]: value } : entry)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (editingProduct) {
        await productsAPI.update(editingProduct.id, formData);
        setSuccess('Product updated successfully');
      } else {
        await productsAPI.create(formData);
        setSuccess('Product created successfully');
      }
      
      fetchProducts();
      setTimeout(() => {
        handleCloseModal();
      }, 1500);
    } catch (error: any) {
      setError(error.message || 'Operation failed');
    }
  };

  const handleToggleStatus = async (productId: string) => {
    try {
      await productsAPI.toggleStatus(productId);
      fetchProducts();
    } catch (error) {
      setError('Failed to toggle product status');
    }
  };

  const handleDelete = async (productId: string) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await productsAPI.delete(productId);
        fetchProducts();
      } catch (error) {
        setError('Failed to delete product');
      }
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchProducts();
  };

  const resetFilters = () => {
    setSearchTerm('');
    setCurrentPage(1);
  };

  if (loading && products.length === 0) {
    return (
      <Container className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
        <Spinner animation="border" role="status" style={{ width: '3rem', height: '3rem', color: '#667eea' }}>
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-3 text-muted">Loading products...</p>
      </Container>
    );
  }

  const canManageProducts = currentUser?.role === 'superadmin';

  return (
    <Container fluid className="py-3">
      <div style={{ width: '95%', maxWidth: '1600px', margin: '0 auto' }}>
      <Row className="mb-4">
        <Col>
          <div className="gradient-card p-4 d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
            <div>
              <h2 className="mb-2 fw-bold">Product List</h2>
              <p className="mb-0 opacity-90">Manage product catalog and configurations</p>
            </div>
            {canManageProducts && (
              <Button variant="primary" onClick={() => handleShowModal()} className="btn-gradient btn-gradient-primary">
                <FiPlus size={16} className="me-2" />Add New Product
              </Button>
            )}
          </div>
        </Col>
      </Row>

      {/* Search */}
      <Row className="mb-4">
        <Col>
          <Card className="modern-card">
            <Card.Body>
              <Form onSubmit={handleSearch}>
                <Row className="g-3">
                  <Col md={8}>
                    <InputGroup>
                      <FormControl
                        placeholder="Search products..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </InputGroup>
                  </Col>
                  <Col md={4}>
                    <div className="d-flex gap-2">
                      <Button type="submit" variant="outline-primary">
                        Search
                      </Button>
                      <Button type="button" variant="outline-secondary" onClick={resetFilters}>
                        Reset
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')} className="modern-card">{error}</Alert>}

      <Row>
        <Col>
          <Card className="modern-card">
            <Card.Body>
              <div className="table-responsive">
                <Table className="modern-table">
                  <thead>
                    <tr>
                      <th>Product Name</th>
                      <th>Description</th>
                      {/* product-level formulas removed */}
                      <th>Status</th>
                      <th>Created By</th>
                      <th>Created Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((product) => (
                      <tr key={product.id}>
                        <td><strong>{product.productName}</strong></td>
                        <td>{product.description || 'No description'}</td>
                        <td style={{ maxWidth: 240, wordBreak: 'break-word' }}>{'—'}</td>
                        <td>
                          <Badge bg={product.isActive ? 'success' : 'secondary'}>
                            {product.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td>{product.createdBy?.name || 'N/A'}</td>
                        <td>{new Date(product.createdAt).toLocaleDateString()}</td>
                        <td>
                          <div className="d-flex flex-wrap gap-1">
                            {canManageProducts && (
                              <>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => handleShowModal(product)}
                                  title="Edit product"
                                  aria-label="Edit product"
                                >
                                  <FiEdit2 size={14} />
                                </Button>
                                
                                <Button
                                  variant={product.isActive ? 'outline-warning' : 'outline-success'}
                                  size="sm"
                                  onClick={() => handleToggleStatus(product.id)}
                                >
                                  {product.isActive ? <><FiUserX size={14} className="me-1" /></> : <><FiUserCheck size={14} className="me-1" />Activate</>}
                                </Button>
                                
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => handleDelete(product.id)}
                                  title="Delete product"
                                  aria-label="Delete product"
                                >
                                  <FiTrash2 size={14} />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {products.length === 0 && (
                <div className="text-center py-4">
                  <p className="text-muted">
                    {canManageProducts 
                      ? 'No products found. Click "Add New Product" to create one.'
                      : 'No products found.'
                    }
                  </p>
                </div>
              )}

              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-3">
                  <Pagination>
                    <Pagination.Prev 
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(currentPage - 1)}
                    />
                    {[...Array(Math.min(5, totalPages))].map((_, index) => {
                      const pageNum = Math.max(1, currentPage - 2) + index;
                      if (pageNum <= totalPages) {
                        return (
                          <Pagination.Item
                            key={pageNum}
                            active={pageNum === currentPage}
                            onClick={() => setCurrentPage(pageNum)}
                          >
                            {pageNum}
                          </Pagination.Item>
                        );
                      }
                      return null;
                    })}
                    <Pagination.Next
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage(currentPage + 1)}
                    />
                  </Pagination>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Product Form Modal */}
      {canManageProducts && (
        <Modal show={showModal} onHide={handleCloseModal} size="lg" centered>
          <Modal.Header closeButton className="gradient-card-header border-0">
            <Modal.Title>
              {editingProduct ? 'Edit Product' : 'Add New Product'}
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body className="p-4">
              {error && <Alert variant="danger" className="modern-card">{error}</Alert>}
              {success && <Alert variant="success" className="modern-card">{success}</Alert>}
              
              <Form.Group className="mb-3">
                <Form.Label>Product Name *</Form.Label>
                <Form.Control
                  type="text"
                  name="productName"
                  value={formData.productName}
                  onChange={handleInputChange}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter product description..."
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  id="water-tanker-checkbox"
                  label="Please check if product is water tanker loading"
                  name="waterTankerEnabled"
                  checked={!!formData.waterTankerEnabled}
                  onChange={handleCheckboxChange}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Calculation Formula</Form.Label>
                <Form.Text className="text-muted">Calculation formula is now configured per system product metadata, not at product level.</Form.Text>
              </Form.Group>

              {formData.waterTankerEnabled && (
                <Card className="mb-3">
                  <Card.Body>
                    <Card.Title>Water Tanker Entries</Card.Title>
                    {formData.tankerEntries.map((entry, idx) => (
                      <Row key={idx} className="align-items-center mb-2">
                        <Col md={5}>
                          <Form.Control
                            placeholder="RFID"
                            value={entry.rfid}
                            onChange={(e) => handleTankerEntryChange(idx, 'rfid', e.target.value)}
                          />
                        </Col>
                        <Col md={5}>
                          <Form.Control
                            placeholder="Tanker Details"
                            value={entry.tankerDetails}
                            onChange={(e) => handleTankerEntryChange(idx, 'tankerDetails', e.target.value)}
                          />
                        </Col>
                        <Col md={2} className="text-end">
                          <Button variant="outline-danger" size="sm" onClick={() => removeTankerEntry(idx)}>Remove</Button>
                        </Col>
                      </Row>
                    ))}

                    <div className="mt-2">
                      <Button size="sm" variant="outline-primary" onClick={addTankerEntry}>+ Add More</Button>
                    </div>
                  </Card.Body>
                </Card>
              )}
            </Modal.Body>
            <Modal.Footer className="border-0">
              <Button variant="secondary" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" className="btn-gradient btn-gradient-primary">
                {editingProduct ? 'Update Product' : 'Create Product'}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      )}
      </div>
    </Container>
  );
};

export default Products;